import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaCircleXmark } from "react-icons/fa6";
import InfiniteScrollComponent from "../../../Components/InfiniteScrollComponent";
import ModalContainer from "../../../Components/ModalContainer";
import { useLazyGetCampaignUsersQuery } from "../../../Data/services/engagementApi";
import { ErrorToast } from "../../../Hooks/useToast";
import {
  STATUS_COLORS,
  campaignLabel,
  formatRelativeTime,
  formatUserDisplay,
} from "../constants/labels";
import styles from "../styles/modal.module.scss";
import { extractLinks, hasNextPage } from "../utils";
import UsersTableSkeleton from "./UsersTableSkeleton";

const PAGE_SIZE = 20;

export default function CampaignSendsModal({ campaign, open, onClose }) {
  const scrollRef = useRef(null);
  const pageRef = useRef(1);
  const [lastPageSize, setLastPageSize] = useState(null);

  const [fetchCampaignUsers, { data, isLoading, isFetching, isError, isSuccess }] =
    useLazyGetCampaignUsersQuery();

  const loadPage = useCallback(
    async (page, limit = PAGE_SIZE) => {
      if (!campaign?.slug) return;

      try {
        const response = await fetchCampaignUsers({
          slug: campaign.slug,
          page,
          limit,
        }).unwrap();
        const pageUsers = response?.data?.users ?? [];
        setLastPageSize(Array.isArray(pageUsers) ? pageUsers.length : 0);
        pageRef.current = page;
      } catch (err) {
        ErrorToast(err?.data?.message || "Failed to load campaign users");
        throw err;
      }
    },
    [campaign?.slug, fetchCampaignUsers]
  );

  useEffect(() => {
    if (!open || !campaign?.slug) return;
    pageRef.current = 1;
    setLastPageSize(null);
    loadPage(1, PAGE_SIZE);
  }, [open, campaign?.slug, loadPage]);

  const links = extractLinks(data);
  const payload = data?.data ?? {};
  const users = Array.isArray(payload.users) ? payload.users : [];
  const totalRecords = links.total_records;
  const perPage = links.per_page || PAGE_SIZE;
  const canLoadMore = hasNextPage(links, {
    loadedCount: users.length,
    lastPageSize,
    perPage,
  });
  const campaignMeta = payload.campaign || campaign || {};
  const title = campaignLabel(campaignMeta.slug, campaignMeta.name);

  const handleNext = useCallback(async () => {
    if (isFetching || !canLoadMore) return;
    const nextPage =
      links.next > links.current ? links.next : pageRef.current + 1;
    await loadPage(nextPage, perPage);
  }, [isFetching, canLoadMore, links.next, links.current, loadPage, perPage]);

  if (!open) return null;

  const hasNext = !isError && users.length > 0 && canLoadMore;
  const showInitialSkeleton = (isLoading || isFetching) && users.length === 0;
  const showEmpty = isSuccess && !isFetching && users.length === 0;
  const isLoadingMore = isFetching && users.length > 0;

  return (
    <ModalContainer handleClose={onClose}>
      <div className={styles.modalPanel}>
        <div className={styles.modalHeader}>
          <div>
            <p className={styles.modalTitle}>{title}</p>
            <p className={styles.modalSubtitle}>
              Campaign users · {totalRecords || users.length} · page{" "}
              {links.current || 1} · showing {users.length}
            </p>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <FaCircleXmark />
          </button>
        </div>

        <div className={styles.modalBody} ref={scrollRef}>
          {isError && users.length === 0 ? (
            <p className="text-danger m-0">Failed to load campaign users.</p>
          ) : showEmpty ? (
            <div className={styles.emptyState}>
              No users found for this campaign.
            </div>
          ) : showInitialSkeleton ? (
            <UsersTableSkeleton rows={8} columns={5} />
          ) : (
            <InfiniteScrollComponent
              next={handleNext}
              hasNext={hasNext}
              isLoading={isLoadingMore}
              rootRef={scrollRef}
              skeleton={<UsersTableSkeleton rows={3} columns={5} />}
            >
              <div className={styles.tableWrap}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Status</th>
                      <th>Sent at</th>
                      <th>Last active</th>
                      <th>Push</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => {
                      const send = user.send || {};
                      const status = (send.status || "").toLowerCase();
                      const display = formatUserDisplay(user, index);
                      const hasFcm = Boolean(user.has_fcm ?? user.fcm_token);

                      return (
                        <tr key={send.slug || user.slug || index}>
                          <td>
                            <div className={styles.userCell}>
                              <span className={styles.userName}>
                                {display.title}
                              </span>
                              {display.subtitle ? (
                                <span className={styles.userMeta}>
                                  {display.subtitle}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <span
                              className={styles.badge}
                              style={{
                                backgroundColor:
                                  STATUS_COLORS[status] || "#6c757d",
                              }}
                            >
                              {status || "—"}
                            </span>
                          </td>
                          <td>{formatRelativeTime(send.sent_at)}</td>
                          <td>{formatRelativeTime(user.last_active_at)}</td>
                          <td>
                            <span
                              className={`${styles.badge} ${
                                hasFcm
                                  ? styles.badgeSent
                                  : styles.badgeSkipped
                              }`}
                            >
                              {hasFcm ? "Eligible" : "No FCM"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </InfiniteScrollComponent>
          )}
        </div>
      </div>
    </ModalContainer>
  );
}
